/**
 * Calculates and returns display flags for different components of a transaction, such as subtotal,
 * discount, fees, tax, and gift card applied amounts.
 *
 * @export
 * @param {number} subTotal - The subtotal amount of the transaction.
 * @param {number} discount - The discount applied to the transaction.
 * @param {number} fees - Any additional fees associated with the transaction.
 * @param {number} tax - The tax applied to the transaction.
 * @param {Transformed.GiftCardBalance[]} giftCardBalance - An array of gift card balances available for the transaction.
 * @param {number} giftCardAppliedAmount - The amount of gift card balance applied to the transaction.
 * @returns {object} An object with boolean flags indicating the display status of each transaction component.
 * @property {boolean} isSubtotal - Indicates whether subtotal exists.
 * @property {boolean} isDiscount - Indicates whether discount is applied.
 * @property {boolean} isFees - Indicates whether fees exist.
 * @property {boolean} isTax - Indicates whether tax exists.
 * @property {boolean} isGiftCardApplied - Indicates whether a gift card balance applied.
 * @property {boolean} showAdditionalSeparator - Indicates whether an additional separator should be
 * shown based on the visibility of the other components.
 */
export const getSubtotalDisplayFlags = (
  subTotal: number,
  discount: number,
  fees: number,
  tax: number,
  giftCardBalance: readonly Transformed.GiftCardBalance[],
  giftCardAppliedAmount: number,
): {
  isSubtotal: boolean,
  isDiscount: boolean,
  isFees: boolean,
  isTax: boolean,
  isGiftCardApplied: boolean,
  showAdditionalSeparator: boolean,
} => {
  const isSubtotal = subTotal > 0 || (subTotal <= 0 && discount > 0)
  const isDiscount = discount > 0
  const isFees = fees > 0
  const isTax = tax > 0
  const isGiftCardApplied = giftCardBalance.length > 0 && giftCardAppliedAmount > 0
  const showAdditionalSeparator = isSubtotal || isDiscount || isFees || isTax || isGiftCardApplied

  return {
    isSubtotal,
    isDiscount,
    isFees,
    isTax,
    isGiftCardApplied,
    showAdditionalSeparator,
  }
}
